import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Eraser, Check, PenTool, Type } from 'lucide-react';

interface ConsentSignatureProps {
  onSign: (signatureData: SignatureData) => void;
  disabled?: boolean;
  patientName?: string;
  isconsentread?: boolean;
}

export interface SignatureData {
  type: 'drawn' | 'typed';
  data: string; // Base64 for drawn, text for typed
  timestamp: string;
}

const ConsentSignature: React.FC<ConsentSignatureProps> = ({
  onSign,
  disabled = false,
  patientName = '',
  isconsentread = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [signatureType, setSignatureType] = useState<'drawn' | 'typed'>('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null);
  const [showConsentTooltip, setShowConsentTooltip] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (isconsentread) {
      setShowConsentTooltip(false);
    }
  }, [isconsentread]);

  const getCanvasCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (disabled) return;
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    setIsDrawing(true);
    setLastPoint(coords);
    setHasDrawn(true);
  };

  const draw = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawing || disabled) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !lastPoint) return;

    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    setLastPoint(coords);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setLastPoint(null);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const isValid = signatureType === 'drawn' ? hasDrawn : typedSignature.trim().length > 0;

  const handleSign = () => {
    if (disabled || !isValid) return;
    if (!isconsentread) {
      setShowConsentTooltip(true);
      return;
    }

    if (signatureType === 'drawn') {
      const canvas = canvasRef.current;
      if (!canvas || !hasDrawn) return;

      const signatureData: SignatureData = {
        type: 'drawn',
        data: canvas.toDataURL('image/png'),
        timestamp: new Date().toISOString(),
      };
      onSign(signatureData);
    } else {
      if (!typedSignature.trim()) return;

      const signatureData: SignatureData = {
        type: 'typed',
        data: typedSignature,
        timestamp: new Date().toISOString(),
      };
      onSign(signatureData);
    }
  };

  const canSign = !disabled && isValid;
  const showDisabledCursor = disabled || !isconsentread;
  const signButton = (
    <Button onClick={handleSign} disabled={!canSign}>
      <Check className="w-4 h-4 mr-2" />
      Sign & Submit
    </Button>
  );

  const consentTooltipOpen = showConsentTooltip && !isconsentread;

  return (
    <div className="space-y-4">
      <Tabs value={signatureType} onValueChange={(v) => setSignatureType(v as 'drawn' | 'typed')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="drawn" className="flex items-center gap-2">
            <PenTool className="w-4 h-4" />
            Draw Signature
          </TabsTrigger>
          <TabsTrigger value="typed" className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            Type Signature
          </TabsTrigger>
        </TabsList>

        <TabsContent value="drawn" className="space-y-4">
          <Tooltip open={consentTooltipOpen} onOpenChange={setShowConsentTooltip}>
            <TooltipTrigger asChild>
              <div
                className={`relative border rounded-lg overflow-hidden bg-white ${
                  showDisabledCursor ? 'cursor-not-allowed' : ''
                }`}
              >
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={150}
                  className={`w-full h-36 touch-none ${
                    showDisabledCursor ? 'cursor-not-allowed' : 'cursor-crosshair'
                  }`}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasDrawn && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-muted-foreground text-sm">Sign here</p>
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Please read the consent form and check the consent box before signing.
            </TooltipContent>
          </Tooltip>
          <div className="flex justify-between items-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearCanvas}
              disabled={disabled || !hasDrawn}
            >
              <Eraser className="w-4 h-4 mr-2" />
              Clear
            </Button>
            <p className="text-xs text-muted-foreground">
              Use your mouse or finger to draw your signature
            </p>
          </div>
        </TabsContent>

        <TabsContent value="typed" className="space-y-4">
          <Tooltip open={consentTooltipOpen} onOpenChange={setShowConsentTooltip}>
            <TooltipTrigger asChild>
              <div className="space-y-2">
                <Label htmlFor="typed-signature">Type Your Full Name</Label>
                <Input
                  id="typed-signature"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder={patientName || 'Enter your full name'}
                  disabled={disabled}
                  className={`text-lg ${showDisabledCursor ? 'cursor-not-allowed' : ''}`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Please read the consent form and check the consent box before signing.
            </TooltipContent>
          </Tooltip>
          <div className="border rounded-lg p-4 bg-muted/50">
            <p className="text-sm text-muted-foreground mb-2">Preview:</p>
            <p
              className="text-2xl italic"
              style={{ fontFamily: 'cursive, "Brush Script MT", "Lucida Handwriting", serif' }}
            >
              {typedSignature || '(Your signature will appear here)'}
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between pt-4 border-t">
        <p className="text-xs text-muted-foreground">
          By signing, you agree to the terms above. Date: {new Date().toLocaleDateString()}
        </p>
        {signButton}
      </div>
    </div>
  );
};

export default ConsentSignature;










